import type { PhoneTheme } from 'magical-trails';

const MUTE_KEY = 'mgtrl:phoneSoundsOff';

export function arrivalSoundsMuted(): boolean {
  try {
    return window.localStorage.getItem(MUTE_KEY) === '1';
  } catch {
    return false;
  }
}

export function setArrivalSoundsMuted(muted: boolean): void {
  try {
    if (muted) window.localStorage.setItem(MUTE_KEY, '1');
    else window.localStorage.removeItem(MUTE_KEY);
  } catch {
    // Storage disabled: the choice just won't outlive this visit.
  }
}

// Two short notes per look, as [frequency Hz, oscillator shape]. Synthesised
// rather than shipped as audio files: nothing to download or precache.
const CHIMES: Record<PhoneTheme, { notes: number[]; wave: OscillatorType }> = {
  modern: { notes: [880, 1174.7], wave: 'sine' },
  blaster: { notes: [523.3, 1046.5], wave: 'square' },
  spyfi: { notes: [987.8, 987.8], wave: 'triangle' },
  steampunk: { notes: [659.3, 830.6], wave: 'triangle' },
  spellbook: { notes: [783.99, 1318.5], wave: 'sine' },
};

let context: AudioContext | undefined;

/**
 * A short chime and a buzz for a new message. Best-effort: browsers only
 * allow audio after the player has touched the page (they always have, by
 * the time a message can arrive), and anything that fails stays silent.
 */
export function announceArrival(theme: PhoneTheme): void {
  try {
    navigator.vibrate?.(70);
  } catch {
    // Not supported, or blocked: fine.
  }
  if (arrivalSoundsMuted()) return;
  try {
    const AudioCtor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!AudioCtor) return;
    context ??= new AudioCtor();
    if (context.state === 'suspended') void context.resume();
    const { notes, wave } = CHIMES[theme];
    const start = context.currentTime + 0.02;
    notes.forEach((frequency, index) => {
      const ctx = context as AudioContext;
      const at = start + index * 0.14;
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.type = wave;
      oscillator.frequency.value = frequency;
      gain.gain.setValueAtTime(0, at);
      gain.gain.linearRampToValueAtTime(
        wave === 'square' ? 0.05 : 0.12,
        at + 0.015
      );
      gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.32);
      oscillator.connect(gain).connect(ctx.destination);
      oscillator.start(at);
      oscillator.stop(at + 0.35);
    });
  } catch {
    // No audio: the banner still shows.
  }
}
